import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { mergePricingConfig, couponIsValid } from './cotizadorConfig';
import { buildProposalDocumentName } from './cotizadorDocumento';
import { 
  Calculator, Users, HardHat, TrendingUp, Shield, FileText, 
  CheckCircle2, MessageCircle, Briefcase, Settings, UserPlus, 
  FileSpreadsheet, User, Loader2, ArrowRight, 
  AlertTriangle, Printer, Rocket, ChevronDown, ChevronUp, FileSignature,
  Info, Smartphone, Lock, Plus, Minus, LayoutGrid, Star, Heart, Gavel, UserMinus,
  Ticket, Tag // Iconos nuevos para cupón
} from 'lucide-react';

// --- CONFIGURACIÓN DE PRECIOS ---
const SURCHARGE_6_MONTHS = 1.07; // +7% Recargo por tiempo corto
const BUNDLE_DISCOUNT_FACTOR = 0.15; // 15% de descuento por Pack Dual
const IVA_RATE = 0.19; // Tasa de IVA
const SMLV_2026 = 1750905;
export const CF7_TIMEOUT_MS = 12000;
// --- TARIFAS 2026 ---
const PRICING_TABLES = {
    rrhh: [
        { min: 0, max: 5, unitPrice: 170000 },
        { min: 6, max: 10, unitPrice: 160000 },
        { min: 11, max: 16, unitPrice: 140000 },
        { min: 17, max: 25, unitPrice: 115000 },
        { min: 26, max: 40, unitPrice: 97000 },
        { min: 41, max: 60, unitPrice: 80000 },
        { min: 61, max: 80, unitPrice: 73000 },
        { min: 81, max: 120, unitPrice: 67000 },
        { min: 121, max: 9999, unitPrice: 62000 }
    ],
    sst_base: [
        { min: 0, max: 5, unitPrice: 110000 },     
        { min: 6, max: 10, unitPrice: 95000 },
        { min: 11, max: 20, unitPrice: 85000 },
        { min: 21, max: 40, unitPrice: 80000 },
        { min: 41, max: 60, unitPrice: 75000 },
        { min: 61, max: 100, unitPrice: 70000 },
        { min: 101, max: 9999, unitPrice: 65000 }
    ],
    sst_risk_factors: {
        1: 1.0, 2: 1.10, 3: 1.30, 4: 1.70, 5: 2.10
    },
    nomina: [
        { min: 0, max: 5, unitPrice: 60000 },
        { min: 6, max: 10, unitPrice: 55000 },
        { min: 11, max: 20, unitPrice: 50000 },
        { min: 21, max: 50, unitPrice: 45000 },
        { min: 51, max: 100, unitPrice: 40000 },
        { min: 101, max: 9999, unitPrice: 35000 }
    ]
};

// --- DATA PARA EL PDF DETALLADO DE SST (ANEXO) ---
const SST_FULL_DETAILS = [
    {
        title: "PAQUETE PUESTA EN MARCHA SST",
        items: [
            "Evaluación inicial del Sistema de Gestión.",
            "Reporte del avance del SG-SST ante el Ministerio de Trabajo.",
            "Reporte del avance ante ARL.",
            "Presentar validación estándares ante ARL.",
            "Designación de responsable del SG-SST.",
            "Realización de la Autoevaluación 0312 de 2019.",
            "Realización del plan de mejoramiento.",
            "Diseño del Plan Anual de Trabajo para el SST.",
            "Diseño del presupuesto para el SST."
        ]
    },
    {
        title: "PAQUETE GESTIÓN DE COMITÉS E INSPECCIONES",
        items: [
            "Conformación y gestión de Comités (COPASST, Convivencia y Brigadas).",
            "Reuniones mensuales de COPASST y Convivencia.",
            "Ejecución de inspecciones (puestos de trabajo).",
            "Actualización de matriz legal.",
            "Actualización de matriz IPVR.",
            "Gestión de cambios (documentación)."
        ]
    },
    {
        title: "PAQUETE PROGRAMAS & SVE",
        items: [
            "Creación de programas y procedimientos del SG-SST.",
            "Documentación y diligenciamiento de formularios internos.",
            "Creación de campañas y/o requisitos del SVE.",
            "Construcción de matriz de casos de salud según SVE.",
            "Inspecciones y análisis de casos según SVE.",
            "Ejecución de actividades de P&P (Prevención y Promoción).",
            "Reporte e investigación de ATEL (Accidentes y Enfermedades Laborales)."
        ]
    },
    {
        title: "PAQUETE VALIDACIÓN DE ESTANDARES",
        items: [
            "Rendición de cuentas del SG-SST.",
            "Revisión de indicadores anuales del SG-SST.",
            "Acompañamiento en la revisión de estándares ARL.",
            "Revisión base de datos trabajadores (Ene-Dic).",
            "Validación de matriz de afiliaciones trabajadores.",
            "Verificación Planilla Seguridad Social (Ene-Dic).",
            "Soporte exámenes médicos ingreso, periódicos y retiro (Ene-Dic).",
            "Revisión informe de condiciones de salud.",
            "Validación medidas preventivas implementadas.",
            "Revisión FURAT reportados.",
            "Validación incapacidades por causa de ATEL.",
            "Evidencia entrega Epps.",
            "Validación inspecciones programadas.",
            "Verificación soporte capacitaciones."
        ]
    },
    {
        title: "OTROS COMPONENTES INCLUIDOS",
        items: [
            "PAQUETE PLAN DE CAPACITACIÓN SST (Diseño, Socialización y Ejecución).",
            "PAQUETE SIMULACRO SST (Coordinación y Certificación IDIGER).",
            "PAQUETE PAT SST (Diseño Plan Anual de Trabajo).",
            "PAQUETE IPVR (Matriz de Peligros y Riesgos)."
        ]
    }
];

// --- DATA PARA EL PDF DETALLADO DE RRHH (ANEXO) ---
const RRHH_FULL_DETAILS = [
    {
        title: "GESTIÓN DE NÓMINA Y SEGURIDAD SOCIAL",
        items: [
            "Liquidación de nómina periódica.",
            "Liquidación de seguridad social (PILA).",
            "Gestión de novedades (incapacidades, licencias, vacaciones).",
            "Emisión de desprendibles de pago.",
            "Certificaciones laborales.",
            "Reporte de nómina electrónica a la DIAN.",
            "Interfaz contable."
        ]
    },
    {
        title: "GESTIÓN DE CULTURA Y BIENESTAR",
        items: [
            "Gestión de comunicaciones internas.",
            "Coordinación de actividades de Bienestar.",
            "Gestión de convenios nuevos para colaboradores.",
            "Mantenimiento de alianzas para colaboradores."
        ]
    },
    {
        title: "GESTIÓN DISCIPLINARIA Y LABORAL",
        items: [
            "Asesoría para presunto incumplimiento por parte del colaborador.",
            "Definición y creación de ruta de gestión disciplinaria.",
            "Levantamiento y construcción de descargos.",
            "Revisión de material probatorio aportado por líder a cargo.",
            "Análisis de los descargos y establecimiento de la falta."
        ]
    },
    {
        title: "CONDICIONES ESPECÍFICAS RR.HH.",
        items: [
            "✔ INCLUYE: 1 proceso de selección al mes (no acumulable).",
            "✘ NO INCLUYE: Paquete de Gestión de Desempeño y Desarrollo.",
            "✘ NO INCLUYE: Exámenes médicos de ingreso/retiro (costo aparte)."
        ]
    }
];

// Icono auxiliar
const PresentationIcon = ({ size, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M2 3h20"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="m7 21 5-5 5 5"/></svg>
);

// --- BASE DE DATOS DE SERVICIOS (REORGANIZADA) ---
const SERVICES_DB_COTIZADOR = [
  // --- SECCIÓN 1: OUTSOURCING INTEGRAL (LOS GRANDES) ---
  { 
    id: 'rrhh_mensual', 
    category: 'main_outsourcing', 
    title: 'Outsourcing RR.HH. Integral', 
    desc: 'Solución completa de Gestión Humana y Nómina.', 
    details: [
        '✅ INCLUYE TODO EL SERVICIO DE NÓMINA', 
        'Gestión de clima y cultura organizacional',
        'Procesos disciplinarios y descargos',
        'Bienestar y fidelización del talento',
        '⚠️ Incluye 1 selección/mes (No acumulable)',
        '⚠️ No incluye Gestión de Desempeño'
    ],
    pdfDetails: RRHH_FULL_DETAILS,
    icon: Users, price: 0, unit: 'Mensual (Plan Integral)', type: 'tiered_unit', table: 'rrhh', defaultChecked: false, color: 'cyan' 
  },
  { 
    id: 'sst_mensual', 
    category: 'main_outsourcing', 
    title: 'Outsourcing SST (Servicio Completo)', 
    desc: 'Gestión integral del Sistema de Seguridad y Salud en el Trabajo.', 
    details: [
        '✅ INCLUYE TODOS LOS PAQUETES SST',
        'Puesta en Marcha y Gestión de Comités',
        'Programas, SVE y Validación de Estándares',
        'Planes de Capacitación y Simulacros',
        'Reporte de indicadores y matriz IPVR',
        '(Ver detalle completo en PDF)'
    ],
    pdfDetails: SST_FULL_DETAILS,
    icon: HardHat, price: 0, unit: 'Mensual (Según Riesgo)', type: 'dynamic_sst_risk', defaultChecked: false, color: 'yellow', highlight: true 
  },

 // --- SECCIÓN 2: ECOSISTEMA SST (PAQUETES) ---

    {
        id: 'sst_acompanamiento',
        category: 'ecosystem_sst',

        title: 'Acompañamiento y Sostenibilidad SG-SST',

        desc: 'Acompañamiento mensual para mantener, controlar y fortalecer el Sistema de Gestión de Seguridad y Salud en el Trabajo.',

        details: [
            'Designación del Responsable del SG-SST',
            'Control del Plan de Trabajo',
            'Auditoría de Evidencias Mensuales',
            'Sesión Estratégica de Planificación',
            'Coordinación de reinversión con ARL'
        ],

        icon: HardHat,

        // Precio fijo mensual
        price: 320000,

        // Información visible
        unit: 'Mensual',

        // Forma de cálculo
        type: 'monthly_fixed',

        // Periodicidad del cobro
        billing: 'monthly',

        color: 'yellow',
        allowQuantity: false
    },

    {
        id: 'sst_accidentes',
        category: 'ecosystem_sst',

        title: 'Gestión Integral de Accidentes de Trabajo',

        desc: 'Acompañamiento integral para la gestión, reporte, seguimiento e investigación de accidentes de trabajo.',

        details: [
            'Inclusión y gestión inmediata del caso',
            'Reporte oficial ante ARL (FURAT)',
            'Direccionamiento a la Red Médica',
            'Monitoreo y Seguimiento Clínico',
            'Investigación Técnica de Causas'
        ],

        icon: Shield,

        // Precio base / referencia Riesgo I
        price: 350000,

        // Es un servicio de una sola vez
        unit: 'Pago Único',

        // El precio depende del nivel de riesgo
        type: 'risk_fixed',

        // Pero NO es mensual
        billing: 'onetime',

        color: 'yellow',
        allowQuantity: false,

        riskPrices: {
            1: 350000,
            2: 430000,
            3: 500000,
            4: 780000,
            5: 980000
        }
    },
  { 
      id: 'paq_puesta_marcha', 
      category: 'ecosystem_sst', 
      title: 'Paquete Puesta en Marcha SST', 
      desc: 'Evaluación inicial, reportes MinTrabajo/ARL y planeación anual.', 
      details: [
          "Evaluación inicial del Sistema de Gestión.",
          "Reporte del avance del SG-SST ante el Ministerio de Trabajo.",
          "Reporte del avance ante ARL.",
          "Presentar validación estándares ante ARL.",
          "Designación de responsable del SG-SST.",
          "Realización de la Autoevaluación 0312 de 2019.",
          "Realización del plan de mejoramiento.",
          "Diseño del Plan Anual de Trabajo y presupuesto SST."
      ], 
      icon: Rocket, price: 1500000, unit: 'Pago Único', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_comites', 
      category: 'ecosystem_sst', 
      title: 'Paquete Gestión de Comités', 
      desc: 'Funcionamiento de COPASST, Convivencia y matriz legal.', 
      details: [
          "Conformación y gestión de Comités (COPASST, Convivencia y Brigadas).",
          "Reuniones mensuales de COPASST y Convivencia.",
          "Ejecución de inspecciones (puestos de trabajo).",
          "Actualización de matriz legal e IPVR.",
          "Gestión de cambios (documentación)."
      ], 
      icon: Users, price: 500000, unit: 'Mensual', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
            id: 'asesoria_sst_hora', 
            category: 'ecosystem_sst', 
            title: 'Asesoría SST (Hora)', 
            desc: 'Consultoría técnica en seguridad y salud.', 
            icon: MessageCircle, 
            price: 120000, 
            unit: 'Por Hora', 
            type: 'onetime_fixed', 
            color: 'yellow', 
            allowQuantity: true 
            },
  { 
      id: 'paq_programas', 
      category: 'ecosystem_sst', 
      title: 'Paquete Programas & SVE', 
      desc: 'Sistemas de Vigilancia Epidemiológica y prevención.', 
      details: [
          "Creación de programas y procedimientos del SG-SST.",
          "Documentación y diligenciamiento de formularios.",
          "Creación de campañas y requisitos del SVE.",
          "Construcción de matriz de casos de salud.",
          "Inspecciones y análisis de casos.",
          "Reporte e investigación de ATEL."
      ], 
      icon: FileText, price: 400000, unit: 'Mensual', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_validacion', 
      category: 'ecosystem_sst', 
      title: 'Paquete Validación Estándares', 
      desc: 'Auditoría completa y revisión anual del sistema.', 
      details: [
          "Rendición de cuentas e indicadores anuales.",
          "Revisión estándares ARL y bases de datos.",
          "Validación afiliaciones, planilla SS y exámenes médicos.",
          "Revisión informe condiciones de salud y FURAT.",
          "Validación incapacidades, Epps e inspecciones."
      ], 
      icon: CheckCircle2, price: 500000, unit: 'Pago Único / 1 Mes', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_plan_cap', 
      category: 'ecosystem_sst', 
      title: 'Paquete Plan Capacitación', 
      desc: 'Diseño y ejecución del plan formativo SST.', 
      details: [
          "Revisión del PAT SST y caracterización de responsables.",
          "Revisión de la oferta de formación de ARL.",
          "Diseño, socialización y ejecución del plan de capacitación."
      ], 
      icon: PresentationIcon, price: 120000, unit: 'Mensual', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_simulacro', 
      category: 'ecosystem_sst', 
      title: 'Paquete Simulacro SST', 
      desc: 'Coordinación de simulacros de emergencia.', 
      details: [
          "Capacitación funciones Brigadistas.",
          "Coordinación y ejecución de 1 simulacro para Sismo.",
          "Solicitud dotación brigada y reuniones previas.",
          "Informe, registro ante IDIGER y certificado."
      ], 
      icon: AlertTriangle, price: 400000, unit: 'Por Unidad', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: true 
  },
  { 
      id: 'paq_pat', 
      category: 'ecosystem_sst', 
      title: 'Paquete PAT SST', 
      desc: 'Plan Anual de Trabajo y condiciones de salud.', 
      details: [
          "Realización Autoevaluación 0312 y plan de mejoramiento.",
          "Definición responsabilidades y tipo de empresa.",
          "Revisión informe condiciones de salud.",
          "Diseño y socialización del PAT SST."
      ], 
      icon: FileSignature, price: 380000, unit: 'Pago Único', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_ipvr', 
      category: 'ecosystem_sst', 
      title: 'Paquete IPVR', 
      desc: 'Identificación de Peligros y Valoración de Riesgos.', 
      details: [
          "Identificación tipo de empresa e inspección locativa.",
          "Revisión manuales de funciones.",
          "Capacitación, encuesta percepción del riesgo.",
          "Diseño matriz y procedimiento IPVR, socialización."
      ], 
      icon: AlertTriangle, price: 350000, unit: 'Pago Único', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: false 
  },
  { 
      id: 'paq_cap_sst', 
      category: 'ecosystem_sst', 
      title: 'Paquete Capacitación Específica', 
      desc: 'Sesión puntual de formación normativa.', 
      details: [
          "Sesión de entendimiento de negocio.",
          "Preparación de material visual y lúdico.",
          "Sesión presencial o virtual.",
          "Certificado de capacitación."
      ], 
      icon: PresentationIcon, price: 200000, unit: 'Pago Único', type: 'onetime_fixed', defaultChecked: false, color: 'yellow', allowQuantity: true 
  },

  // --- SECCIÓN 3: ECOSISTEMA RRHH ---
  { 
    id: 'nomina', 
    category: 'ecosystem_rrhh', 
    title: 'Outsourcing de Nómina', 
    desc: 'Liquidación exacta, seguridad social y gestión de novedades.', 
    details: [
        'Liquidación de nómina y seguridad social',
        'Gestión de novedades y ausentismos',
        'Certificaciones y desprendibles',
        'Reporte de nómina electrónica DIAN',
        'Interfaz contable'
    ],
    // RESTAURADO COLOR VERDE
    icon: FileSpreadsheet, price: 0, unit: 'Por Colaborador', type: 'tiered_unit', table: 'nomina', defaultChecked: false, color: 'green' 
  },

  // NUEVO SERVICIO UBICADO AL LADO DE SELECCIÓN DOMÉSTICO Y DEBAJO DE SELECCIÓN DE PERSONAL
  {
      id: 'paquete_rit',
      category: 'ecosystem_rrhh',
      title: 'Reglamento Interno de Trabajo',
      desc: 'Estructuración y actualización legal de tu reglamento de manera dinámica.',
      details: [
          'Adaptación a modalidades Híbrida, Remota o Presencial',
          'Cláusulas personalizadas según tamaño y sector de la empresa',
          'Actualización de políticas de desconexión laboral y herramientas TIC',
          'Revisión y alineación con el Código Sustantivo del Trabajo',
          '⚠️ Nota: Para empresas de gran envergadura o alta complejidad regulatoria, el valor final se ajustará con un asesor experto.'
      ],
      icon: FileText, price: 553500, unit: 'Proyecto Único', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: false
  },
  // --- Ajuste en SECCIÓN 3: ECOSISTEMA RRHH ---
  { 
    id: 'seleccion', 
    category: 'ecosystem_rrhh', 
    title: 'Selección de Personal', 
    desc: 'Reclutamiento de talento idóneo según nivel salarial.', 
    details: ['Filtro de HV', 'Pruebas psicotécnicas', 'Entrevistas'],
    icon: UserPlus, 
    price: 0, // Se calculará dinámicamente
    unit: 'Por Vacante', 
    type: 'salary_based_selection', // Nuevo tipo de cálculo
    color: 'pink', 
    allowQuantity: true 
},

  
  // NUEVO SERVICIO: SELECCIÓN DOMÉSTICA
  {
      id: 'seleccion_domestico',
      category: 'ecosystem_rrhh',
      title: 'Selección Personal Doméstico',
      desc: 'Proceso ágil para contratación de servicio doméstico.',
      details: [
          'Publicación en redes gratuitas de la vacante',
          'Recepción y validación de hoja de vida',
          'Agendar entrevista aspirante y empleador'
      ],
      // Color pink para mantener identidad de selección
      icon: UserPlus, price: 300000, unit: 'Por Vacante', type: 'onetime_fixed', defaultChecked: false, color: 'pink', allowQuantity: true
  },
    // NUEVO SERVICIO: Hora asesoria de RRHH
{ 
    id: 'asesoria_rrhh_hora', 
    category: 'ecosystem_rrhh', 
    title: 'Asesoría RR.HH. (Hora)', 
    desc: 'Consultoría puntual en gestión humana.', 
    icon: MessageCircle, 
    price: 80000, 
    unit: 'Por Hora', 
    type: 'onetime_fixed', 
    color: 'cyan', 
    allowQuantity: true 
},
  {
      id: 'rrhh_onboarding',
      category: 'ecosystem_rrhh',
      title: 'Gestión de Onboarding',
      desc: 'Proceso de ingreso, inducción y adaptación.',
      details: [
          'Creación del plan de onboarding',
          'Realizar proceso de pre-onboarding y alistamiento',
          'Gestión de programación del onboarding',
          'Ejecución del plan de Onboarding',
          'Sesión de bienvenida y presentación',
          'Sesión de Inducción según temas aprobados',
          'Seguimiento de adaptación'
      ],
      icon: UserPlus, price: 150000, unit: 'Por Persona', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true
  },
  {
      id: 'rrhh_desempeno',
      category: 'ecosystem_rrhh',
      title: 'Gestión de Desempeño',
      desc: 'Evaluaciones 360°, KPIs y planes de acción.',
      details: [
          'Realizar evaluaciones de periodo de prueba',
          'Diseño e implementación de evaluación 360°',
          'Definición y seguimiento de KPIs',
          'Planes de acción individuales',
          'Cierre y reporte de Q (evaluación trimestral)'
      ],
      icon: TrendingUp, price: 20000, unit: 'Por Persona', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true
  },
  {
      id: 'rrhh_cultura',
      category: 'ecosystem_rrhh',
      title: 'Cultura y Bienestar',
      desc: 'Estrategia de fidelización y clima organizacional.',
      details: [
          'Gestión de comunicaciones internas',
          'Coordinación de actividades de Bienestar',
          'Gestión de convenios nuevos para colaboradores',
          'Mantenimiento de alianzas para colaboradores'
      ],
      icon: Heart, price: 8000, unit: 'Por Persona', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true
  },
  {
      id: 'rrhh_disciplinario',
      category: 'ecosystem_rrhh',
      title: 'Gestión Disciplinaria',
      desc: 'Manejo de procesos disciplinarios y descargos.',
      details: [
          'Asesoría para presunto incumplimiento',
          'Definición y creación de ruta disciplinaria',
          'Levantamiento y construcción de descargos',
          'Revisión de material probatorio',
          'Análisis de descargos y establecimiento de la falta'
      ],
      icon: Gavel, price: 180000, unit: 'Por Proceso', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true
  },
  {
      id: 'rrhh_retiro',
      category: 'ecosystem_rrhh',
      title: 'Desvinculación y Retiro',
      desc: 'Legalización de retiro y liquidaciones.',
      details: [
          'Legalización formal del retiro',
          'Gestión del acta de entrega',
          'Liquidación de prestaciones sociales',
          'Reporte de novedad de retiro a la Seguridad Social'
      ],
      icon: UserMinus, price: 200000, unit: 'Por Persona', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true
  },

  { 
      id: 'afiliaciones', 
      category: 'ecosystem_rrhh', 
      title: 'Paquete de Afiliaciones', 
      desc: 'Trámites de seguridad social esenciales.', 
      details: [
          'Afiliación a EPS, AFP, ARL y Caja de Compensación', 
          'Radicación de documentos ante entidades', 
          'Entrega de soportes de afiliación'
        ], 
        icon: Settings, price: 250000, unit: 'Por Trámite', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true 
  },
  { 
      id: 'afiliaciones_full', 
      category: 'ecosystem_rrhh', 
      title: 'Paquete Afiliaciones FULL', 
      desc: 'Gestión completa de ingreso + Legal.', 
      details: [
          'Afiliación a EPS, AFP, ARL y Caja de Compensación', 
          'Radicación de documentos ante entidades', 
          'Entrega de soportes de afiliación', 
          'Elaboración de Contrato Laboral', 
          'Agendamiento de Exámenes Médicos', 
          'Carpeta digital de ingreso'
        ], 
        icon: FileSignature, price: 350000, unit: 'Por Trámite', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', highlight: true, allowQuantity: true 
  },
  { 
      id: 'juridico_hora', 
      category: 'ecosystem_rrhh', 
      title: 'Asesoría Jurídica Laboral', 
      desc: 'Consultas, contratos o descargos puntuales.', 
      details: [
          'Concepto jurídico o revisión de contrato', 
          'Acompañamiento en descargos', 
          'Respuesta a derechos de petición'
        ], 
        icon: Shield, price: 308000, unit: 'Por Hora/Documento', type: 'onetime_fixed', defaultChecked: false, color: 'cyan', allowQuantity: true 
  },

  // --- SECCIÓN 4: PROYECTOS ORGANIZACIONALES ---
  { id: 'estructura_org', category: 'projects', title: 'Estructura Organizacional', desc: 'Ordenamiento de roles, manuales y procesos.', details: ['Diseño de organigrama', 'Manuales de funciones y perfiles', 'Mapa de procesos clave', 'Reglamento Interno de Trabajo'], icon: TrendingUp, price: 3075000, unit: 'Proyecto Único', type: 'onetime_fixed', defaultChecked: false, color: 'purple' },
  { id: 'innovacion', category: 'projects', title: 'Consultoría en Innovación', desc: 'Transformación digital y metodologías ágiles.', details: ['Diagnóstico de madurez digital', 'Implementación de metodologías ágiles', 'Gestión del cambio'], icon: Rocket, price: 3690000, unit: 'Proyecto Base', type: 'onetime_fixed', defaultChecked: false, color: 'rose' },
];

// --- COMPONENTE PRINCIPAL ---
// officialQuote is supplied by the future backend adapter; never generated here.
const Cotizador = ({ officialQuote = null } = {}) => {
  const leadRequests = useRef(new Map());
  const actionPending = useRef(false);
  const quoteNumber = typeof officialQuote?.quoteNumber === 'string' ? officialQuote.quoteNumber.trim() : '';

  useEffect(() => {
      document.body.classList.add('zaphy-proposal-print');
      return () => document.body.classList.remove('zaphy-proposal-print');
  }, []);

  const { service } = useParams();

  // --- ESTADO DE CONFIGURACIÓN ---
  const [pricingConfig] = useState(() => mergePricingConfig({
      tables: PRICING_TABLES,
      services: SERVICES_DB_COTIZADOR,
      ivaRate: IVA_RATE,
      timeSurcharge: SURCHARGE_6_MONTHS,
      bundleDiscount: BUNDLE_DISCOUNT_FACTOR
  }, window.ZAPHY_TARIFAS));
// --- COMPONENTE CAMBIO PARA PROCESOS DE SELECCION CON LA CONSTANTE DE SALARIO ---
  const [inputs, setInputs] = useState({ idType: 'nit', idNumber: '', name: '', company: '', email: '', phone: '', numEmployees: 10, riskLevel: 1, contractDuration: '12'});
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectionSalaries, setSelectionSalaries] = useState({ 1: 1750905 });
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [openDetails, setOpenDetails] = useState({});
  // ESTADO TOTALES
  const [totals, setTotals] = useState({ monthly: 0, onetime: 0, subtotal: 0, iva: 0, grandTotal: 0, discount: 0, discountActive: false, couponVal: 0, subtotalNeto: 0 });
  const [calculatedServices, setCalculatedServices] = useState([]); 
  const [status, setStatus] = useState('idle');
  const [notification, setNotification] = useState(null); 
  
  // ESTADO DE CUPONES
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code: 'xanji5', discount: 0.05 }

  const [expandedSections, setExpandedSections] = useState({
      main: true,
      sst: false,
      rrhh: false,
      projects: false
  });

  const toggleSection = (section) => {
      setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatCurrency = (val) => {
    if (!val || val < 0) return '$ 0';
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);
  };

  const updateQuantity = (e, id, delta) => {
    e.stopPropagation(); 
    setServiceQuantities(prev => {
        const currentQty = prev[id] || 1;
        const newQty = Math.max(1, currentQty + delta);
        return { ...prev, [id]: newQty };
    });
  };

  // --- MANEJADOR DE CUPONES ACTUALIZADO ---
const handleApplyCoupon = () => {
    const code = couponCode.trim().toLowerCase();
    // Reset si está vacío
    if (!code) {
        setAppliedCoupon(null);
        setNotification('Cupón removido');
        setTimeout(() => setNotification(null), 2000);
        return;
    }

    const coupon = pricingConfig.coupons.find(item => item.code === code);
    if (couponIsValid(coupon)) {
        setAppliedCoupon(coupon);
        setNotification(`¡Cupón aplicado! Descuento del ${coupon.discount * 100}%${coupon.serviceIds.length ? ' sobre los servicios elegibles' : ''}`);
    } else {
        setAppliedCoupon(null);
        setNotification('⚠️ Cupón no válido o fuera de vigencia');
    }
    setTimeout(() => setNotification(null), 3000);
};

  useEffect(() => {
      const mapServiceId = { 
          'nomina': 'nomina', 
          'sst': 'sst_mensual', 
          'recursos-humanos': 'rrhh_mensual', 
          'seleccion-personal': 'seleccion', 
          'estructura-organizacional': 'estructura_org', 
          'EstructuraOrganizacional': 'estructura_org',
          'servicios-independientes': 'afiliaciones', 
          'juridico': 'juridico_hora' 
      };

      if (service && pricingConfig.services.some(item => item.id === mapServiceId[service])) {
          const serviceId = mapServiceId[service];
          setSelectedServices(prev => { 
              if (!prev.includes(serviceId)) return [...prev, serviceId]; 
              return prev; 
          });
          setServiceQuantities(prev => ({ ...prev, [serviceId]: 1 }));
          window.scrollTo({ top: 0, behavior: 'smooth' });
      }
  }, [service, pricingConfig]);

  useEffect(() => {
    let monthlyTotal = 0; let onetimeTotal = 0; let discountVal = 0;
    const count = Math.max(1, parseInt(inputs.numEmployees) || 1);
    const risk = parseInt(inputs.riskLevel) || 1;
    const timeFactor = inputs.contractDuration === '6' ? pricingConfig.timeSurcharge : 1.0;
    
    const hasSST = selectedServices.includes('sst_mensual');
    const hasRRHH = selectedServices.includes('rrhh_mensual');
    const isBundleActive = hasSST && hasRRHH;

    const getRangeData = (table, c) => { const range = table.find(r => c >= r.min && c <= r.max); return range ? range : table[table.length - 1]; };

    const updatedServices = pricingConfig.services.map(service => {
        let finalPrice = 0; let originalPrice = 0; let unitPrice = 0; let note = '';
        const itemQuantity = serviceQuantities[service.id] || 1;
        const billingCount = (service.id === 'sst_mensual' || service.id === 'rrhh_mensual') ? Math.max(5, count) : count;
        const isMinimumApplied = count < 5 && (service.id === 'sst_mensual' || service.id === 'rrhh_mensual');
        const applyTimeFactor = (service.id === 'rrhh_mensual' || service.id === 'sst_mensual' || service.id === 'nomina');
        const currentFactor = applyTimeFactor ? timeFactor : 1.0;
        const bundleDiscount = (isBundleActive && (service.id === 'sst_mensual' || service.id === 'rrhh_mensual')) ? (1 - pricingConfig.bundleDiscount) : 1.0;

        
                // DENTRO DEL MAP DE updatedServices Para Calcular cuanto deberia pagar por proceso de seleccion
                if (service.type === 'salary_based_selection') {
                const itemQuantity = serviceQuantities[service.id] || 1;
                let totalSelectionPrice = 0;
                let requiereAsesor = false;

                for (let i = 1; i <= itemQuantity; i++) {
                    const salary = selectionSalaries[i] || SMLV_2026;
                    let priceForThisVacant = 0;

                    if (salary > 10000000) {
                        priceForThisVacant = 4000000;
                        requiereAsesor = true;
                    } else if (salary <= SMLV_2026) {
                        priceForThisVacant = 950000;
                    } else {
                        const factor = (salary - SMLV_2026) / (10000000 - SMLV_2026);
                        priceForThisVacant = 950000 + (factor * (4000000 - 950000));
                    }
                    totalSelectionPrice += priceForThisVacant;
                }

                // --- CRÍTICO: Estas líneas envían el valor al totalizador general ---
                unitPrice = totalSelectionPrice / itemQuantity;
                finalPrice = totalSelectionPrice; 
                originalPrice = finalPrice;
                
                // Nota que aparece en la tarjeta
                note = requiereAsesor 
                    ? "⚠️ Salario Directivo: Requiere atención personalizada" 
                    : `${itemQuantity} vacante(s) configurada(s)`;
            }
        
       else if (service.type === 'tiered_unit') {
            const table = pricingConfig.tables[service.table];
            const range = getRangeData(table, count);
            unitPrice = range.unitPrice * currentFactor; 
            const subtotal = unitPrice * billingCount;
            const serviceDiscount = (service.id === 'rrhh_mensual') ? bundleDiscount : 1.0;
            originalPrice = subtotal; 
            finalPrice = subtotal * serviceDiscount;
            note = `${formatCurrency(unitPrice)} x ${billingCount} colab.`;
            if (isMinimumApplied) note += ' (Cobro Mínimo: 5)';

        } else if (service.type === 'dynamic_sst_risk') {
            const range = getRangeData(pricingConfig.tables.sst_base, count);
            const baseUnit = range.unitPrice;
            const riskFactor = pricingConfig.tables.sst_risk_factors[risk] || 1.0;
            unitPrice = baseUnit * riskFactor * currentFactor;
            const subtotal = unitPrice * billingCount;
            const serviceDiscount = (isBundleActive) ? bundleDiscount : 1.0;
            originalPrice = subtotal; 
            finalPrice = subtotal * serviceDiscount;
            note = `Riesgo ${risk} (${formatCurrency(unitPrice)} x ${billingCount})`;
            if (isMinimumApplied) note += ' (Cobro Mínimo: 5)';

        } else if (service.type === 'monthly_fixed' || service.type === 'risk_fixed') {

            unitPrice = service.type === 'risk_fixed'
                ? service.riskPrices[risk]
                : service.price;

            const multiplier = service.allowQuantity === true
                ? itemQuantity
                : 1;

            finalPrice = unitPrice * multiplier;
            originalPrice = finalPrice;

            const billingLabel =
                service.billing === 'onetime'
                    ? 'Pago Único'
                    : 'Mensual';

            if (service.type === 'risk_fixed') {
                note = `Nivel de Riesgo ${risk} · ${billingLabel}`;
            } else {
                note = billingLabel;
            }

            if (service.allowQuantity === true) {
                note += ` · ${multiplier} x ${formatCurrency(unitPrice)}`;
            }
        } else {
            let basePrice = service.price;
            let appliedNote = service.unit;

            if (service.id === 'afiliaciones' && itemQuantity >= 4) {
                basePrice = 230000; 
                appliedNote = 'Desc. por Volumen aplicado (>3)';
            }
            if (service.id === 'afiliaciones_full' && itemQuantity >= 4) {
                basePrice = 320000; 
                appliedNote = 'Desc. por Volumen aplicado (>3)';
            }

            unitPrice = basePrice;
            const multiplier = service.allowQuantity ? itemQuantity : 1;
            
            finalPrice = unitPrice * multiplier; 
            originalPrice = service.price * multiplier;
            
            if (service.allowQuantity) {
                note = `${multiplier} x ${formatCurrency(unitPrice)}`;
                if (appliedNote !== service.unit) note += ` (${appliedNote})`;
            } else {
                note = appliedNote;
            }
        }

       const isMonthly =
          service.billing === 'monthly' ||
                (
                    !service.billing &&
                    (
                        service.type === 'monthly_fixed' ||
                        (!service.isCustom && (
                            service.unit.toLowerCase().includes('mensual') ||
                            service.category === 'main_outsourcing' ||
                            service.id === 'nomina'
                        ))
                    )
                );

                if (selectedServices.includes(service.id)) {
                    if (isMonthly) {
                        monthlyTotal += finalPrice;
                    } else {
                        onetimeTotal += finalPrice;
                    }
                }

                return {
                    ...service,
                    finalPrice,
                    originalPrice,
                    unitPrice,
                    note,
                    qty: service.allowQuantity ? itemQuantity : 1,
                    hasDiscount: (
                        isBundleActive &&
                        (service.id === 'sst_mensual' || service.id === 'rrhh_mensual')
                    )
                };
        }); // <-- AGREGA ESTA LÍNEA
    if (hasSST && hasRRHH) {
        const sst = updatedServices.find(s => s.id === 'sst_mensual');
        const rrhh = updatedServices.find(s => s.id === 'rrhh_mensual');
        if (sst && rrhh) {
            discountVal = (sst.originalPrice + rrhh.originalPrice) * pricingConfig.bundleDiscount;
        }
    }

    // --- LÓGICA DE CUPÓN MODIFICADA EN EL EFFECT DE TOTALES ---
        let subtotalCalc = monthlyTotal + onetimeTotal;
        let couponDiscountValue = 0;

        if (couponIsValid(appliedCoupon)) {
            const eligibleSubtotal = updatedServices
                .filter(s => selectedServices.includes(s.id) && (!appliedCoupon.serviceIds.length || appliedCoupon.serviceIds.includes(s.id)))
                .reduce((sum, s) => sum + s.finalPrice, 0);
            couponDiscountValue = eligibleSubtotal * appliedCoupon.discount;
            subtotalCalc -= couponDiscountValue;
        }

        const ivaCalc = subtotalCalc * pricingConfig.ivaRate;
        const grandTotalCalc = subtotalCalc + ivaCalc;

        setCalculatedServices(updatedServices);
        setTotals({
             monthly: monthlyTotal,
             onetime: onetimeTotal,
             subtotal: monthlyTotal + onetimeTotal, 
             couponVal: couponDiscountValue, 
             subtotalNeto: subtotalCalc, 
             iva: ivaCalc,
             grandTotal: grandTotalCalc,
             discount: discountVal,
             discountActive: isBundleActive
    });
  }, [inputs, selectedServices, serviceQuantities, pricingConfig, appliedCoupon, selectionSalaries]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (e.target.type === 'number' && value < 0) return;
    setInputs(prev => ({ ...prev, [name]: value }));
  };

  const toggleService = (id) => {
    if (!pricingConfig.services.some(item => item.id === id)) return;
    if (id === 'afiliaciones' && selectedServices.includes('afiliaciones_full')) { 
        setSelectedServices(prev => prev.filter(s => s !== 'afiliaciones_full').concat(id)); 
        setServiceQuantities(prev => ({...prev, [id]: 1}));
        return; 
    }
    if (id === 'afiliaciones_full' && selectedServices.includes('afiliaciones')) { 
        setSelectedServices(prev => prev.filter(s => s !== 'afiliaciones').concat(id)); 
        setServiceQuantities(prev => ({...prev, [id]: 1}));
        return; 
    }
    if (id === 'rrhh_mensual' && !selectedServices.includes(id)) {
        if (selectedServices.includes('nomina')) {
            setSelectedServices(prev => prev.filter(s => s !== 'nomina').concat(id));
            setNotification('Nota: El Outsourcing de RR.HH. ya incluye Nómina. Se ha actualizado tu selección.');
            setTimeout(() => setNotification(null), 5000);
            return;
        }
    }
    if (id === 'nomina' && !selectedServices.includes(id)) {
        if (selectedServices.includes('rrhh_mensual')) {
             setSelectedServices(prev => prev.filter(s => s !== 'rrhh_mensual').concat(id));
             setNotification('Se ha cambiado al plan solo Nómina (RR.HH. desactivado).');
             setTimeout(() => setNotification(null), 4000);
             return;
        }
    }
    if (selectedServices.includes(id)) {
        setSelectedServices(prev => prev.filter(item => item !== id));
    } else {
        setSelectedServices(prev => [...prev, id]);
        setServiceQuantities(prev => ({ ...prev, [id]: 1 }));
    }
  };

  const toggleDetails = (e, id) => { e.stopPropagation(); setOpenDetails(prev => ({ ...prev, [id]: !prev[id] })); };

  const saveLeadToBackend = async (data) => {
      const controller = new AbortController();
      let timeout;
      let diagnostic = { kind: 'network', httpStatus: null, cf7Status: null };
      try {
          const siteUrl = window.zaphyData?.siteUrl || 'https://zaphy.com.co'; 
          const formId = '7720'; 

          const formData = new FormData();
          formData.append('your-name', data.name);
          formData.append('your-email', data.email);
          formData.append('your-phone', data.phone);
          formData.append('your-subject', `Cotización Web - ${data.company || data.name}`);
          
          const servicesList = calculatedServices
              .filter(s => selectedServices.includes(s.id))
              .map(s => {
                  const qtyPrefix = s.allowQuantity ? `(${s.qty}x) ` : '';
                  const salaries = s.id === 'seleccion' ? `; salarios: ${Array.from({ length: s.qty }, (_, i) => formatCurrency(selectionSalaries[i + 1] || SMLV_2026)).join(', ')}` : '';
                  return `- ${qtyPrefix}${s.title}${['monthly_fixed', 'risk_fixed'].includes(s.type) ? ` (${s.note})` : ''}: ${formatCurrency(s.finalPrice)}${salaries}`;
              })
              .join('\n');
              
          const messageBody = `
            Empresa: ${data.company || 'N/A'}
            Contacto: ${data.name}
            Email: ${data.email}
            Teléfono: ${data.phone}
            Fecha de generación: ${data.date}
            
            --- PARÁMETROS ---
            Empleados: ${data.numEmployees}
            Riesgo: ${data.riskLevel}
            Contrato: ${data.contractDuration} Meses

            --- SERVICIOS SELECCIONADOS ---
            ${servicesList}

            --- TOTALES ---
            Subtotal: ${formatCurrency(data.total.subtotal)}
            Descuento Paquete Full (${data.total.discountActive ? pricingConfig.bundleDiscount * 100 : 0}%): -${formatCurrency(data.total.discount)}
            Cupón aplicado: ${totals.couponVal > 0 ? appliedCoupon?.code : 'Ninguno'}
            Porcentaje cupón: ${totals.couponVal > 0 ? appliedCoupon.discount * 100 : 0}%
            Descuento Cupón: -${formatCurrency(data.total.couponVal)}
            Subtotal Neto: ${formatCurrency(data.total.subtotalNeto)}
            IVA (${pricingConfig.ivaRate * 100}%): ${formatCurrency(data.total.iva)}
            TOTAL NETO: ${formatCurrency(data.total.grandTotal)}
            
            (Cotización generada automáticamente desde la web)
          `;
          
          formData.append('your-message', messageBody);
          formData.append('_wpcf7', formId);
          formData.append('_wpcf7_version', '5.9');
          formData.append('_wpcf7_locale', 'es_ES');
          formData.append('_wpcf7_unit_tag', `wpcf7-f${formId}-p1-o1`);
          formData.append('_wpcf7_container_post', '0');

          // Bound the entire request, including response parsing, so the proposal remains usable.
          await Promise.race([
              (async () => {
                  const response = await fetch(`${siteUrl}/wp-json/contact-form-7/v1/contact-forms/${formId}/feedback`, {
                      method: 'POST',
                      body: formData,
                      signal: controller.signal
                  });
                  diagnostic = { ...diagnostic, httpStatus: response.status, contentType: response.headers?.get('content-type'), kind: 'invalid_response' };
                  const result = await response.json();
                  diagnostic = {
                      ...diagnostic, kind: response.ok === false ? 'http' : 'cf7',
                      cf7Status: result?.status ?? null, code: result?.code ?? null,
                      message: result?.message ?? null,
                      invalidFields: Array.isArray(result?.invalid_fields) ? result.invalid_fields.map(({ field, message }) => ({ field, message })) : []
                  };
                  if (response.ok === false || result?.status !== 'mail_sent') throw new Error('CF7 did not confirm delivery');
              })(),
              new Promise((_, reject) => {
                  timeout = setTimeout(() => {
                      diagnostic = { ...diagnostic, kind: 'timeout' };
                      reject(new Error('CF7 timeout'));
                      controller.abort();
                  }, CF7_TIMEOUT_MS);
              })
          ]);

      } catch (e) {
          // No contact values or request payload are logged. Keep the server's diagnostic intact.
          console.error('CF7: captura no confirmada', { ...diagnostic, error: e.message });
          return { status: 'error' };
      } finally {
          clearTimeout(timeout);
      }
      return { status: 'success' };
  };

  const validateAndAct = async (action) => {
     if (!inputs.name || !inputs.email || !inputs.phone) {
         setNotification('⚠️ Por favor completa tus datos (Nombre, Email, Teléfono) para generar la propuesta.');
         setTimeout(() => setNotification(null), 4000);
         window.scrollTo({ top: 0, behavior: 'smooth' });
         return;
     }

     if (actionPending.current) return;
     actionPending.current = true;
     setStatus('loading');
     setNotification(null);

     // Dates and document metadata are excluded: printing/reopening is not a new lead.
     const fingerprint = JSON.stringify({ inputs, services: calculatedServices.filter(s => selectedServices.includes(s.id)).map(({ id, qty, finalPrice, note }) => ({ id, qty, finalPrice, note })), totals, coupon: appliedCoupon, selectionSalaries });
     const leadData = { ...inputs, date: new Date().toISOString(), total: totals };
     if (!leadRequests.current.has(fingerprint)) {
         leadRequests.current.set(fingerprint, saveLeadToBackend(leadData));
     }
     const saveResult = await leadRequests.current.get(fingerprint);
     if (saveResult.status !== 'success') leadRequests.current.delete(fingerprint);
     actionPending.current = false;
     setStatus('idle');

     if (action === 'whatsapp') handleWhatsAppClick();
     else if (action === 'print') {
         const previousTitle = document.title;
         document.title = buildProposalDocumentName({ quoteNumber, company: inputs.company, contactName: inputs.name,
             selectedServices, services: calculatedServices, fullPackage: totals.discountActive });
         try { window.print(); } finally { document.title = previousTitle; }
     }
  };

  const handleWhatsAppClick = () => {
      const selectedNames = calculatedServices
      .filter(s => selectedServices.includes(s.id))
      .map(s => {
          let extraInfo = '';
          if (s.id === 'seleccion') {
              // Crea una lista de los salarios configurados
              const salList = Array.from({ length: s.qty })
                  .map((_, i) => `%0a   - Vacante ${i+1}: ${formatCurrency(selectionSalaries[i+1] || SMLV_2026)}`)
                  .join('');
              extraInfo = salList;
          } else if (s.allowQuantity) {
              extraInfo = ` (${s.qty} Unds.)`;
          }
          return `• ${s.title}${extraInfo}${['monthly_fixed', 'risk_fixed'].includes(s.type) ? ` (${s.note})` : ''}: ${formatCurrency(s.finalPrice)}`;
      })
      .join('%0a');
      
      const packText = totals.discountActive ? `%0a🔥 *ZAPHY PAQUETE FULL ACTIVADO (15% OFF)*` : '';
      const couponText = appliedCoupon ? `%0a🎫 *Cupón Válido (${appliedCoupon.discount * 100}% OFF):* -${formatCurrency(totals.couponVal)}` : '';

      const conditionsText = `%0a%0a📜 *CONDICIONES COMERCIALES:*%0a` +
        `1. Pago 100% anticipado. Validez 15 días.%0a` +
        `2. Atención Virtual/Presencial L-V 8-5pm.%0a` +
        (selectedServices.includes('rrhh_mensual') ? `- Outsourcing RRHH: Incluye 1 selección/mes. Adicional: 25% salario.%0a` : ``) +
        `- No incluye: estudios seguridad, polígrafo, viáticos fuera Bogotá, trámites legales directos.%0a` +
        `Nota: He leído y acepto los términos expuestos en la cotización web.`;

      const message = `Hola *Zaphy*, mi cotización:%0a%0a` +
          `🏢 *Empresa:* ${inputs.company || inputs.name || 'N/A'}%0a` +
          `👤 *Contacto:* ${inputs.name} (${inputs.phone})%0a` +
          `📧 *Email:* ${inputs.email}%0a` +
          `👥 *Empleados:* ${inputs.numEmployees} | ⏳ *Contrato:* ${inputs.contractDuration} Meses%0a` +
          `⚠️ *Riesgo:* ${inputs.riskLevel}%0a%0a` +
          `📋 *Servicios:*%0a${selectedNames}${packText}${couponText}%0a%0a` +
          `💰 *Subtotal:* ${formatCurrency(totals.subtotal)}%0a` +
          `💰 *IVA (19%):* ${formatCurrency(totals.iva)}%0a` +
          `💰 *TOTAL NETO:* ${formatCurrency(totals.grandTotal)}` +
          `${conditionsText}`;

      window.open(`https://wa.me/573114044203?text=${encodeURIComponent(message.replace(/%0a/g, '\n'))}`, '_blank');
  };

  const getStyle = (service, isSelected) => {
      const baseStyle = "relative cursor-pointer p-5 rounded-2xl border-2 transition-all duration-300 flex flex-col justify-between h-full hover:shadow-lg";
      const colors = { green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-100 hover:border-green-300', purple: isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-100 hover:border-purple-300', cyan: isSelected ? 'border-cyan-500 bg-cyan-50' : 'border-gray-100 hover:border-cyan-300', yellow: isSelected ? 'border-yellow-400 bg-yellow-50' : 'border-gray-100 hover:border-yellow-300', pink: isSelected ? 'border-pink-500 bg-pink-50' : 'border-gray-100 hover:border-pink-300', indigo: isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-indigo-300', rose: isSelected ? 'border-[#8B0C3C] bg-pink-50' : 'border-gray-100 hover:border-[#8B0C3C]' };
      const highlightStyle = service.highlight && !isSelected ? 'border-indigo-200 bg-indigo-50/30' : '';
      return `${baseStyle} ${colors[service.color] || colors.purple} ${highlightStyle}`;
  };

  const getIconColor = (color) => {
      const colors = { green: 'text-green-600 bg-green-100', purple: 'text-purple-600 bg-purple-100', cyan: 'text-cyan-600 bg-cyan-100', yellow: 'text-yellow-600 bg-yellow-100', pink: 'text-pink-600 bg-pink-100', indigo: 'text-indigo-600 bg-indigo-100', rose: 'text-[#8B0C3C] bg-pink-100' };
      return colors[color] || 'text-purple-600 bg-purple-100';
  };

  const toast = notification && (
      <div className="proposal-toast no-print" role="status" aria-live="polite" aria-atomic="true">
          <Info size={20} aria-hidden="true" />
          <div className="proposal-toast-content">
              <p>{notification}</p>
          </div>
          <button className="proposal-toast-close" aria-label="Cerrar notificación" onClick={() => setNotification(null)}>×</button>
      </div>
  );

  return (
    <div className="cotizador-page pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      <style>{`
        .proposal-overlay { font-family: 'Poppins', sans-serif; }
          /* ESTILOS PARA LA SECCIÓN DETALLADA DE ANEXOS */
          .annex-section { margin-top: 14px; break-inside: auto; page-break-inside: auto; } /* Eliminado el page-break-before */
          .annex-title { font-size: 14px; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid; padding-bottom: 5px; margin-bottom: 8px; }

          /* Colores específicos para Anexos */
          .sst-theme .annex-title { color: #d97706; border-color: #E1FC01; }
          .sst-theme .annex-item { border-left: 3px solid #E1FC01; background: #fdfce8; }
          .sst-theme .annex-h4 { color: #92400e; }

          .rrhh-theme .annex-title { color: #0891b2; border-color: #22d3ee; }
          .rrhh-theme .annex-item { border-left: 3px solid #22d3ee; background: #ecfeff; }
          .rrhh-theme .annex-h4 { color: #155e75; }

          .annex-grid { display: block; column-count: 2; column-gap: 14px; }
          .annex-item { break-inside: avoid; page-break-inside: avoid; margin-bottom: 8px; padding: 7px; border-radius: 6px; }
          .annex-h4 { font-size: 11px; font-weight: bold; margin-bottom: 4px; text-transform: uppercase; }
          .annex-ul { padding-left: 12px; margin: 0; }
          .annex-li { font-size: 10px; color: #4b5563; margin-bottom: 2px; }
        @media screen {
          .proposal-overlay { display: none; }
          .proposal-overlay.is-open { display: block; position: fixed; inset: 0; z-index: 2147483000; overflow-y: auto; overscroll-behavior: contain; background: #f3f4f6; }
          .proposal-toolbar { position: sticky; top: 0; z-index: 1; display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; padding: 16px 24px; background: white; border-bottom: 1px solid #ddd6fe; }
          .proposal-actions { display: flex; flex-wrap: wrap; gap: 8px; }
          .proposal-toolbar button { padding: 10px 16px; border: 1px solid #ddd6fe; border-radius: 10px; }
          .proposal-toolbar button:disabled { opacity: .5; cursor: not-allowed; }
          .proposal-toolbar button:focus-visible { outline: 3px solid #0891b2; outline-offset: 2px; }
          .proposal-toast { position: fixed; right: 24px; bottom: 24px; z-index: 2147483100; display: flex; align-items: flex-start; gap: 10px; width: min(380px, calc(100vw - 32px)); max-height: min(40dvh, 320px); overflow: auto; padding: 16px; border: 1px solid #a78bfa; border-radius: 14px; background: #2e1065; color: white; box-shadow: 0 8px 30px #0003; font-size: 14px; line-height: 1.5; }
          .proposal-toast > svg { flex-shrink: 0; margin-top: 4px; }
          .proposal-toast-content { flex: 1; min-width: 0; overflow-wrap: anywhere; }
          .proposal-toast button { min-height: 44px; text-decoration: underline; }
          .proposal-toast-close { flex-shrink: 0; width: 44px; font-size: 24px; }
          .proposal-toast button:focus-visible { outline: 2px solid #67e8f9; outline-offset: 2px; }
          .proposal-actions { min-width: 0; max-width: 100%; }
          .proposal-toolbar button { min-height: 44px; max-width: 100%; overflow-wrap: anywhere; }
          .proposal-toolbar button:hover { background: #f3e8ff; }
          .quote-main-actions button { min-height: 44px; }
          .quote-main-actions button:focus-visible { outline: 2px solid #67e8f9; outline-offset: 3px; }
          .proposal-overlay.is-open { padding-bottom: 120px; }
          .preview-visible .annex-section { margin-top: 24px; }
          .preview-visible .annex-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; column-count: auto; }
          .preview-visible .annex-title { font-size: 16px; }
          .preview-visible .annex-item { min-width: 0; margin: 0; padding: 14px; }
          .preview-visible .annex-h4 { font-size: 13px; }
          .preview-visible .annex-li { font-size: 12px; line-height: 1.5; }
          .preview-visible .annex-ul { list-style: disc; padding-left: 18px; }
          .preview-visible .print-table { table-layout: fixed; }
          .preview-visible .print-table td { vertical-align: top; overflow-wrap: anywhere; }
          .preview-visible .proposal-client > div { min-width: 0; }
          .preview-visible .proposal-totals { max-width: 100%; }
          .preview-visible .proposal-totals > div { gap: 8px; }
          @media (max-width: 800px) {
            .preview-visible .annex-grid { grid-template-columns: minmax(0, 1fr); }
            .proposal-toolbar { align-items: stretch; }
            .proposal-actions { flex-wrap: wrap; }
          }
          @media (max-width: 640px) {
            .proposal-toolbar { flex-direction: column; }
            .proposal-actions { width: 100%; }
            .proposal-actions button { flex: 1 1 140px; }
            .preview-visible .proposal-client { flex-direction: column; gap: 16px !important; }
            .preview-visible .print-header > div { min-width: 0; max-width: 100%; flex-wrap: wrap; }
            .proposal-toast { right: 16px; bottom: 16px; }
          }
          .print-only { display: none; }
          .print-only.preview-visible { display: block; max-width: 1000px; margin: 24px auto; padding: 24px; background: white; overflow-wrap: anywhere; line-height: 1.4; box-shadow: 0 4px 24px #0001; }
          .preview-visible .print-header { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 20px; border-bottom: 2px solid #6d28d9; padding-bottom: 16px; margin-bottom: 16px; }
          .preview-visible .print-table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 12px; }
          .preview-visible .print-table th { background: #f3e8ff; color: #4c1d95; text-align: left; padding: 8px; }
          .preview-visible .print-table td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
          .preview-visible .print-footer { margin-top: 16px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 11px; }
          .preview-visible .cond-title { display: block; margin-top: 12px; font-weight: bold; }
          @media (max-width: 640px) { .print-only.preview-visible { margin: 12px; padding: 16px; } .proposal-toolbar { padding: 12px; } }
        }
        @media print {
          /* The document portal is a sibling of the app root, whose min-height is 100vh. */
          body.zaphy-proposal-print > :not(.proposal-overlay) { display: none !important; }
          body.zaphy-proposal-print > .proposal-overlay { min-height: 0 !important; max-height: none !important; margin: 0 !important; padding: 0 !important; break-before: auto !important; break-after: auto !important; }
          .proposal-overlay { display: block !important; position: static !important; overflow: visible !important; height: auto !important; }
          body { overflow: visible !important; }
          @page { size: auto; margin: 1.0cm; } 
          body { background-color: white!important; margin:0!important; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-size: 12px; padding: 0!important; }
          .no-print, nav, footer, .whatsapp-float, button, .fixed { display: none !important; }
          .print-only { display: block !important; position: relative; width: 100%; background: white; z-index: 9999; }
          
          /* Control de saltos de página para evitar espacios en blanco */
          .cotizador-page { padding: 0 !important; min-height: 0 !important; background: white !important; }
          .print-section { break-inside: avoid; page-break-inside: avoid; margin-bottom: 12px; }
          .print-table thead { display: table-header-group; }
          .annex-title, .annex-h4, .cond-title { break-after: avoid; page-break-after: avoid; }
          .print-list li, .print-signature { break-inside: avoid; page-break-inside: avoid; }
          .print-list { orphans: 3; widows: 3; }
          .print-signature { break-before: avoid; page-break-before: avoid; }
          #printable-area { overflow-wrap: anywhere; line-height: 1.4; }
          .print-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 11px; }
          .print-table th { background-color: #f3e8ff !important; color: #4c1d95; text-align: left; padding: 6px; border-bottom: 1px solid #d8b4fe; }
          .print-table td { padding: 6px; border-bottom: 1px solid #e5e7eb; color: #1f2937; vertical-align: top; }
          .print-table tr { break-inside: avoid; page-break-inside: avoid; }
          
          .print-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #6d28d9; padding-bottom: 10px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; }
          .print-footer { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #6b7280; text-align: left; break-inside: auto; page-break-inside: auto; line-height: 1.4; }
          
          ul.print-list { padding-left: 15px; margin: 5px 0; }
          ul.print-list li { margin-bottom: 3px; }
          .cond-title { font-weight: bold; color: #4c1d95; margin-top: 8px; display: block; font-size: 10px; }
          
          
        }
      `}</style>
      
      {toast && createPortal(toast, document.body)}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 no-print">
        <div className="text-center mb-12">
           <span className="text-purple-600 font-bold tracking-[0.2em] uppercase text-xs mb-3 block">Cotizador Inteligente 2026</span>
           <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">Diseña tu Plan <span className="text-purple-600">Zaphy</span></h1>
           <p className="text-xl text-gray-500 max-w-2xl mx-auto">Personaliza tu paquete. Al aumentar el número de empleados o servicios, los valores se ajustan automáticamente.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Columna Izquierda: Datos y Parámetros */}
            <div className="lg:col-span-1 space-y-6">
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Lock className="text-green-600" size={16}/> <span className="ml-1">Tus Datos</span></h3>
                    <div className="space-y-4">
                        <div><label className="block text-xs font-bold text-gray-500 mb-1">Soy...</label><select name="idType" value={inputs.idType} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none"><option value="nit">Empresa (NIT)</option><option value="cc">Persona Natural (Cédula)</option></select></div>
                        {inputs.idType === 'nit' && (<div className="animate-[fadeIn_0.3s]"><label className="block text-xs font-bold text-gray-500 mb-1">Razón Social</label><input type="text" name="company" value={inputs.company} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none" placeholder="Nombre de tu empresa" /></div>)}
                        <input type="text" name="name" value={inputs.name} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-cyan-500 outline-none" placeholder={inputs.idType === 'nit' ? "Nombre del Contacto" : "Tu Nombre Completo"} />
                        <input type="email" name="email" value={inputs.email} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-cyan-500 outline-none" placeholder="Correo Electrónico" />
                        <div className="relative"><Smartphone className="absolute top-3 left-3 text-gray-400" size={18} /><input type="tel" name="phone" value={inputs.phone} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 pl-10 text-sm focus:ring-2 focus:ring-cyan-500 outline-none" placeholder="Celular / WhatsApp" /></div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Settings className="text-gray-500" size={20}/> Parámetros</h3>
                    <div className="space-y-4">
                       <div className="grid grid-cols-2 gap-4">
                           <div><label className="block text-xs font-bold text-gray-500 mb-1">Total Empleados</label><input type="number" name="numEmployees" value={inputs.numEmployees} onChange={handleInputChange} min="1" className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none" /></div>
                           <div><label className="block text-xs font-bold text-gray-500 mb-1">Nivel Riesgo</label><select name="riskLevel" value={inputs.riskLevel} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none"><option value="1">I (Bajo)</option><option value="2">II (Bajo)</option><option value="3">III (Medio)</option><option value="4">IV (Alto)</option><option value="5">V (Máximo)</option></select></div>
                       </div>
                       <div><label className="block text-xs font-bold text-gray-500 mb-1">Duración Contrato</label><div className="flex bg-gray-100 p-1 rounded-xl"><button onClick={() => setInputs({...inputs, contractDuration: '12'})} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.contractDuration === '12' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500'}`}>12 Meses</button><button onClick={() => setInputs({...inputs, contractDuration: '6'})} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.contractDuration === '6' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500'}`}>6 Meses</button></div></div>
                    </div>
                </div>

                {/* NUEVO MÓDULO DE CUPONES (MEJORADO) */}
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 mt-6">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Ticket className="text-pink-500" size={20}/> Cupón de Descuento</h3>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input 
                            type="password" 
                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl p-2 text-sm outline-none focus:ring-2 focus:ring-pink-500 w-full"
                            placeholder="Código promocional"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                        />
                        <button 
                            onClick={handleApplyCoupon}
                            className="bg-pink-600 hover:bg-pink-700 text-white px-3 py-2 rounded-xl text-xs font-bold transition-colors whitespace-nowrap w-full sm:w-auto"
                        >
                            Aplicar
                        </button>
                    </div>
                    {appliedCoupon && (
                        <div className="mt-2 text-xs text-green-600 font-bold flex items-center gap-1">
                            <CheckCircle2 size={12}/> Descuento del {appliedCoupon.discount * 100}% aplicado
                        </div>
                    )}
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                
                                {/* 1. OUTSOURCING INTEGRAL */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleSection('main')}
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg"><Star size={20} className="fill-cyan-600"/></div>
                            <h3 className="text-lg font-bold text-gray-800">Outsourcing Integral</h3>
                            {totals.discountActive && (
                                <span className="hidden sm:inline-block bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-full animate-pulse shadow-lg">
                                    ¡ZAPHY PAQUETE FULL ACTIVADO!
                                </span>
                            )}
                        </div>
                        {expandedSections.main ? <ChevronUp size={20} className="text-gray-400"/> : <ChevronDown size={20} className="text-gray-400"/>}
                    </div>
                    
                    {expandedSections.main && (
                        <div className="p-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 animate-[fadeIn_0.3s]">
                            {/* CAMBIO AQUÍ: Filtrar por 'main_outsourcing' en lugar de 'ecosystem_rrhh' */}
                            {calculatedServices.filter(s => s.category === 'main_outsourcing').map(service => (
                                <ServiceCard 
                                    key={service.id} 
                                    service={service} 
                                    isSelected={selectedServices.includes(service.id)} 
                                    onToggle={() => toggleService(service.id)} 
                                    isOpen={openDetails[service.id]} 
                                    onToggleDetails={(e) => toggleDetails(e, service.id)} 
                                    getStyle={getStyle} 
                                    getIconColor={getIconColor} 
                                    formatCurrency={formatCurrency}
                                    showQuantity={service.allowQuantity && selectedServices.includes(service.id)}
                                    quantity={service.qty}
                                    onIncrease={(e) => updateQuantity(e, service.id, 1)}
                                    onDecrease={(e) => updateQuantity(e, service.id, -1)}
                                    selectionSalaries={selectionSalaries}
                                    setSelectionSalaries={setSelectionSalaries}
                                    SMLV_2026={SMLV_2026}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* 2. ECOSISTEMA SST */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                   <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleSection('sst')}
                   >
                       <div className="flex items-center gap-3">
                           <div className="p-2 bg-yellow-100 text-yellow-600 rounded-lg"><HardHat size={20}/></div>
                           <div>
                                <h3 className="text-lg font-bold text-gray-800">Ecosistema SST</h3>
                                <p className="text-xs text-gray-500">Seguridad y Salud en el Trabajo (Modular)</p>
                           </div>
                       </div>
                       {expandedSections.sst ? <ChevronUp size={20} className="text-gray-400"/> : <ChevronDown size={20} className="text-gray-400"/>}
                   </div>

                   {expandedSections.sst && (
                       <div className="p-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 animate-[fadeIn_0.3s]">
                           {calculatedServices.filter(s => s.category === 'ecosystem_sst').map(service => (
                               <ServiceCard 
                                   key={service.id} 
                                   service={service} 
                                   isSelected={selectedServices.includes(service.id)} 
                                   onToggle={() => toggleService(service.id)} 
                                   isOpen={openDetails[service.id]} 
                                   onToggleDetails={(e) => toggleDetails(e, service.id)} 
                                   getStyle={getStyle} 
                                   getIconColor={getIconColor} 
                                   formatCurrency={formatCurrency}
                                   showQuantity={service.allowQuantity && selectedServices.includes(service.id)}
                                   quantity={service.qty}
                                   onIncrease={(e) => updateQuantity(e, service.id, 1)}
                                   onDecrease={(e) => updateQuantity(e, service.id, -1)}
                               />
                           ))}
                       </div>
                   )}
                </div>

                {/* 3. ECOSISTEMA RRHH */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                   <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleSection('rrhh')}
                   >
                       <div className="flex items-center gap-3">
                           <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg"><Users size={20}/></div>
                           <div>
                                <h3 className="text-lg font-bold text-gray-800">Ecosistema RR.HH.</h3>
                                <p className="text-xs text-gray-500">Gestión Humana, Nómina y Legal</p>
                           </div>
                       </div>
                       {expandedSections.rrhh ? <ChevronUp size={20} className="text-gray-400"/> : <ChevronDown size={20} className="text-gray-400"/>}
                   </div>

                   {expandedSections.rrhh && (
                       <div className="p-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 animate-[fadeIn_0.3s]">
                           {calculatedServices.filter(s => s.category === 'ecosystem_rrhh').map(service => (
                               <ServiceCard 
                                   key={service.id} 
                                   service={service} 
                                   isSelected={selectedServices.includes(service.id)} 
                                   onToggle={() => toggleService(service.id)} 
                                   isOpen={openDetails[service.id]} 
                                   onToggleDetails={(e) => toggleDetails(e, service.id)} 
                                   getStyle={getStyle} 
                                   getIconColor={getIconColor} 
                                   formatCurrency={formatCurrency}
                                   showQuantity={service.allowQuantity && selectedServices.includes(service.id)}
                                   quantity={service.qty}
                                   onIncrease={(e) => updateQuantity(e, service.id, 1)}
                                   onDecrease={(e) => updateQuantity(e, service.id, -1)}
                                   selectionSalaries={selectionSalaries}
                                   setSelectionSalaries={setSelectionSalaries}
                                   SMLV_2026={SMLV_2026}
                               />
                           ))}
                       </div>
                   )}
                </div>

                {/* 4. PROYECTOS */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                   <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleSection('projects')}
                   >
                       <div className="flex items-center gap-3">
                           <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><Rocket size={20}/></div>
                           <h3 className="text-lg font-bold text-gray-800">Proyectos y Consultoría</h3>
                       </div>
                       {expandedSections.projects ? <ChevronUp size={20} className="text-gray-400"/> : <ChevronDown size={20} className="text-gray-400"/>}
                   </div>

                   {expandedSections.projects && (
                       <div className="p-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 animate-[fadeIn_0.3s]">
                           {calculatedServices.filter(s => s.category === 'projects').map(service => (<ServiceCard key={service.id} service={service} isSelected={selectedServices.includes(service.id)} onToggle={() => toggleService(service.id)} isOpen={openDetails[service.id]} onToggleDetails={(e) => toggleDetails(e, service.id)} getStyle={getStyle} getIconColor={getIconColor} formatCurrency={formatCurrency} showQuantity={service.allowQuantity && selectedServices.includes(service.id)} quantity={service.qty} onIncrease={(e) => updateQuantity(e, service.id, 1)} onDecrease={(e) => updateQuantity(e, service.id, -1)} />))}
                       </div>
                   )}
                </div>

                {/* BARRA FLOTANTE */}
                <div className="sticky bottom-4 z-30 no-print">
                    <div className="bg-gray-900 text-white p-6 rounded-3xl shadow-2xl flex flex-col md:flex-row justify-between items-center gap-6 border border-gray-800">
                        <div className="text-center md:text-left w-full md:w-auto">
                            <p className="text-gray-400 text-xs uppercase font-bold tracking-widest mb-1">Total Estimado</p>
                            <div className="flex flex-wrap md:flex-nowrap justify-center md:justify-start items-center gap-4 md:gap-6">
                                <div className="text-center md:text-left">
                                    <p className="text-lg font-bold text-purple-400">{formatCurrency(totals.monthly)} <span className="text-xs text-gray-400">+ IVA</span></p>
                                    <p className="text-xs text-gray-400">Mensual</p>
                                </div>

                                <div className="hidden md:block h-10 w-px bg-gray-700"></div>

                                <div className="text-center md:text-left">
                                    <p className="text-lg font-bold text-cyan-400">{formatCurrency(totals.onetime)} <span className="text-xs text-gray-400">+ IVA</span></p>
                                    <p className="text-xs text-gray-400">Pago Único</p>
                                </div>
                                
                                {/* MOSTRAR DESCUENTO CUPÓN EN BARRA FLOTANTE */}
                                {totals.couponVal > 0 && (
                                    <>
                                        <div className="hidden md:block h-10 w-px bg-gray-700"></div>
                                        <div className="text-center md:text-left w-full md:w-auto">
                                            <p className="text-lg font-bold text-green-400">-{formatCurrency(totals.couponVal)}</p>
                                            <p className="text-xs text-gray-400">Desc. Cupón</p>
                                        </div>
                                    </>
                                )}

                                <div className="hidden md:block h-10 w-px bg-gray-700"></div>
                                <div className="text-center md:text-left w-full md:w-auto border-t border-gray-700 pt-2 md:pt-0 md:border-0">
                                    <p className="text-2xl font-bold text-yellow-400">{formatCurrency(totals.grandTotal)}</p>
                                    <p className="text-xs text-gray-400">TOTAL NETO (INCL. IVA)</p>
                                </div>
                            </div>
                            {totals.discountActive && (<p className="text-[10px] text-green-400 mt-1 font-bold animate-pulse">* Incluye descuento del 15% por Paquete Full</p>)}
                        </div>
                        <div className="quote-main-actions flex flex-wrap gap-3 w-full md:w-auto justify-center">
                           <button disabled={status === 'loading'} title="Imprimir o guardar como PDF" onClick={() => validateAndAct('print')} className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"><Printer size={24} aria-hidden="true"/> Descargar cotización</button>
                           <button disabled={status === 'loading'} onClick={() => validateAndAct('whatsapp')} className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-3 transition-all shadow-lg hover:shadow-green-500/20"><MessageCircle size={24}/> WhatsApp</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* --- ÁREA DE IMPRESIÓN (PDF) --- */}
      {createPortal(<div className="proposal-overlay">
      <div id="printable-area" className="print-only">
         <div className="print-header">
             <div className="flex items-center gap-4">
                <img src="https://zaphy.com.co/wp-content/uploads/2021/07/cropped-LOGO-ZAPHY.png" alt="Zaphy" style={{height: '60px'}} />
                <div><h2 style={{color: '#4c1d95', margin: 0, fontSize: '18px'}}>Zaphy Gestión Integral</h2><p style={{margin: 0, fontSize: '10px', color: '#6b7280'}}>NIT: 901.940.322-8 | Bogotá, Medellín, Cali - Colombia</p></div>
             </div>
             <div style={{textAlign: 'right'}}>{quoteNumber && <h1 style={{color: '#6d28d9', margin: 0}}>{`COTIZACIÓN N.º ${quoteNumber}`}</h1>}<p style={{margin: 0, fontSize: '12px'}}>Fecha: {new Date().toLocaleDateString()}</p><p style={{margin: 0, fontSize: '12px'}}>Vigencia: 15 Días</p></div>
         </div>
         <div style={{display: 'flex', gap: '40px'}} className="print-section proposal-client">
             <div style={{flex: 1}}>
                <h3 style={{fontSize: '14px', borderBottom: '1px solid #ddd'}}>Datos del Cliente</h3>
                <p style={{fontSize: '12px', marginBottom: '2px'}}><strong>{inputs.company || inputs.name || 'Cliente General'}</strong></p>
                {inputs.name.trim() && <p style={{fontSize: '12px', marginBottom: '2px'}}>Atn: {inputs.name.trim()}</p>}
                {inputs.email && <p style={{fontSize: '12px', marginBottom: '2px'}}>{inputs.email}</p>}
                {inputs.phone && <p style={{fontSize: '12px'}}>Tel: {inputs.phone}</p>}
             </div>
             <div style={{flex: 1}}>
                <h3 style={{fontSize: '14px', borderBottom: '1px solid #ddd'}}>Parámetros</h3>
                <p style={{fontSize: '12px', marginBottom: '2px'}}><strong>Empleados:</strong> {inputs.numEmployees}</p><p style={{fontSize: '12px', marginBottom: '2px'}}><strong>Contrato:</strong> {inputs.contractDuration} Meses</p><p style={{fontSize: '12px'}}><strong>Nivel de Riesgo:</strong> {inputs.riskLevel}</p>
             </div>
         </div>
         <p className="print-section" style={{fontSize: '11px'}}>
             {inputs.name.trim() ? `Hola, ${inputs.name.trim()}. ` : ''}Hemos preparado esta propuesta para {inputs.company || inputs.name || 'usted'} de acuerdo con los servicios seleccionados.
         </p>
         <table className="print-table">
             <thead><tr><th style={{width: '70%'}}>Servicio / Detalle</th><th style={{width: '30%'}}>Valor Total</th></tr></thead>
             <tbody>
                 {calculatedServices.some(s => selectedServices.includes(s.id)) ? calculatedServices.filter(s => selectedServices.includes(s.id)).map((s, i) => (
                     <tr key={i} style={{pageBreakInside: 'avoid'}}>
                         <td>
                             <strong>{s.title}</strong> {s.allowQuantity ? <span style={{fontSize: '10px', backgroundColor: '#eee', padding: '2px 5px', borderRadius: '4px'}}>Cant: {s.qty}</span> : ''}
                             {/* DESGLOSE DE SALARIOS EN EL PDF */}
                                {s.id === 'seleccion' && (
                                    <div style={{ 
                                        fontSize: '10px', 
                                        color: '#4c1d95', 
                                        backgroundColor: '#f5f3ff', 
                                        padding: '5px', 
                                        borderRadius: '4px', 
                                        marginTop: '5px',
                                        marginBottom: '5px',
                                        borderLeft: '2px solid #8b5cf6' 
                                    }}>
                                        <strong>Salarios base:</strong> {Array.from({ length: s.qty }).map((_, i) => formatCurrency(selectionSalaries[i+1] || SMLV_2026)).join(' | ')}
                                    </div>
                                )}
                             <ul style={{margin: '5px 0', paddingLeft: '15px', color: '#555'}}>{s.details && s.details.map((d, idx) => <li key={idx} style={{marginBottom: '2px'}}>{d}</li>)}</ul>
                         </td>
                         <td style={{textAlign: 'right'}}>
                            {s.hasDiscount && <div style={{textDecoration: 'line-through', color: '#999', fontSize: '10px'}}>{formatCurrency(s.originalPrice)}</div>}
                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{formatCurrency(s.finalPrice)}</span><div style={{fontSize: '10px', color: '#666'}}>{s.note}</div>
                         </td>
                     </tr>
                 )) : (<tr><td colSpan="2" style={{textAlign: 'center', padding: '20px'}}>No hay servicios seleccionados</td></tr>)}
                 {totals.discountActive && (<tr style={{backgroundColor: '#ecfdf5'}}><td style={{color: '#047857', fontWeight: 'bold'}}>DESCUENTO ZAPHY PAQUETE FULL (15%)</td><td style={{textAlign: 'right', color: '#047857', fontWeight: 'bold'}}>- {formatCurrency(totals.discount)}</td></tr>)}
             </tbody>
         </table>
         <div style={{display: 'flex', justifyContent: 'flex-end'}} className="print-section">
             <div className="proposal-totals" style={{width: '300px', textAlign: 'right'}}>
                 <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}><span style={{color: '#666'}}>Subtotal Mensual:</span><span style={{fontWeight: 'bold', fontSize: '14px', color: '#7c3aed'}}>{formatCurrency(totals.monthly)}</span></div>
                 <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}><span style={{color: '#666'}}>Subtotal Único:</span><span style={{fontWeight: 'bold', fontSize: '14px', color: '#0891b2'}}>{formatCurrency(totals.onetime)}</span></div>
                 <div style={{borderTop: '1px solid #ddd', margin: '5px 0'}}></div>
                 <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}><span style={{color: '#333'}}>SUBTOTAL BRUTO:</span><span style={{fontWeight: 'bold', fontSize: '14px'}}>{formatCurrency(totals.subtotal)}</span></div>
                 
                 {appliedCoupon && (
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#16a34a'}}>
                        <span style={{fontWeight: 'bold'}}>DESCUENTO CUPÓN ({appliedCoupon.discount * 100}%):</span>
                        <span style={{fontWeight: 'bold', fontSize: '14px'}}>- {formatCurrency(totals.couponVal)}</span>
                    </div>
                 )}

                 <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}><span style={{color: '#333'}}>IVA (19%):</span><span style={{fontWeight: 'bold', fontSize: '14px'}}>{formatCurrency(totals.iva)}</span></div>
                 <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '10px', backgroundColor: '#f3f4f6', padding: '10px', borderRadius: '5px'}}><span style={{color: '#000', fontWeight: 'bold'}}>TOTAL NETO:</span><span style={{fontWeight: 'bold', fontSize: '18px', color: '#000'}}>{formatCurrency(totals.grandTotal)}</span></div>
             </div>
         </div>
         
         {/* ANEXO TÉCNICO SST (Si aplica) */}
         {selectedServices.includes('sst_mensual') && (
            <div className="sst-annex sst-theme annex-section">
                <div className="annex-title">ANEXO TÉCNICO: ALCANCE DETALLADO SST</div>
                <div className="annex-grid">
                    {SST_FULL_DETAILS.map((section, idx) => (
                        <div key={idx} className="annex-item">
                            <div className="annex-h4">{section.title}</div>
                            <ul className="annex-ul">
                                {section.items.map((item, idj) => (
                                    <li key={idj} className="annex-li">{item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
         )}

         {/* ANEXO TÉCNICO RRHH (Si aplica) */}
         {selectedServices.includes('rrhh_mensual') && (
            <div className="rrhh-annex rrhh-theme annex-section">
                <div className="annex-title">ANEXO TÉCNICO: ALCANCE DETALLADO RR.HH.</div>
                <div className="annex-grid">
                    {RRHH_FULL_DETAILS.map((section, idx) => (
                        <div key={idx} className="annex-item">
                            <div className="annex-h4">{section.title}</div>
                            <ul className="annex-ul">
                                {section.items.map((item, idj) => (
                                    <li key={idj} className="annex-li">{item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
         )}

         <div className="print-footer">
             <span className="cond-title">1. CONDICIONES COMERCIALES</span>
             <ul className="print-list">
                 <li><strong>Forma de Pago:</strong> 100% mes anticipado.</li>
                 <li><strong>Modalidad de Trabajo:</strong> Virtual y presencial. Canal abierto de WhatsApp L-V 8am-5pm.</li>
                 <li><strong>Validez de la oferta:</strong> 15 días calendario contados a partir de la fecha de envío de la presente propuesta.</li>
             </ul>
             <span className="cond-title">2. OBLIGACIONES DEL CLIENTE</span>
             <ul className="print-list">
                 <li>Entregar la información requerida para llevar a cabo las etapas de cada proceso.</li>
                 <li>Garantizar los recursos técnicos y financieros (salas de reunión, refrigerios, consumibles de papelería).</li>
                 <li>Suministrar usuario en software de gestión de RRHH y usuario de IPS.</li>
                 <li>Suministrar usuario del operador de pagos y/o otras claves del área de RRHH vigentes.</li>
                 <li>Las plataformas para publicar vacantes de empleo pagas (computrabajo.com y/o empleo.com).</li>
                 <li>Correo del dominio de la empresa para gestionar los requerimientos a título del área de RRHH y/o SST.</li>
             </ul>
             <span className="cond-title">3. CONDICIONES ESPECIALES DEL CONTRATO</span>
             <ul className="print-list">
                 {selectedServices.includes('rrhh_mensual') && (<li><strong>Outsourcing RRHH:</strong> La propuesta incluye 1 proceso de selección al mes. En caso de requerir un proceso de selección adicional, se facturará el 25% del valor del salario de la vacante a contratar, el cual no es acumulativo por los meses no requeridos.</li>)}
                 <li>La propuesta no incluye estudios de seguridad, visita domiciliaria y/o prueba de polígrafo, en caso de requerir estos servicios, se podrán cotizar de forma independiente.</li>
                 <li>El alcance legal del presente outsourcing es asesoría para toma de decisiones, manejo de situaciones en materia laboral y/o establecer la carga probatoria, si el proceso requiere trámites directos de un abogado, se podrá cotizar el proceso con el buffet de abogados asociado a Zaphy Gestión Integral de forma independiente.</li>
                 <li>Los viáticos fuera del perímetro urbano de Bogotá estarán a cargo del cliente.</li>
                 <li>El servicio aplica para la retención en la fuente del 4%.</li>
             </ul>
             <div className="print-signature" style={{marginTop: '12px', borderTop: '1px solid #ddd', paddingTop: '10px', width: '300px', maxWidth: '100%'}}>
                 <div style={{fontSize: '11px', color: '#333'}}>
                    <p style={{margin: '0', fontWeight: 'bold'}}>Andrés Castañeda</p>
                    <p style={{margin: '0'}}>Zaphy Gestión Integral SAS</p>
                    <p style={{margin: '0'}}>Gerencia Comercial</p>
                    <p style={{margin: '0', fontSize: '9px'}}>Móvil: 311 404 4203 - 300 750 3796 | comercial@zaphy.com.co</p>
                 </div>
             </div>
         </div>
      </div>
      </div>, document.body)}
    </div>
    );

};

// Componente Auxiliar para Tarjeta de Servicio
// --- COMPONENTE AUXILIAR PARA TARJETA DE SERVICIO ---
const ServiceCard = ({ 
    service, isSelected, onToggle, isOpen, onToggleDetails, 
    getStyle, getIconColor, formatCurrency, 
    showQuantity, quantity, onIncrease, onDecrease,
    selectionSalaries, setSelectionSalaries, SMLV_2026 
}) => (
    <div onClick={onToggle} className={getStyle(service, isSelected)}>
        <div className="flex items-start justify-between mb-2">
            <div className={`p-2 rounded-lg ${getIconColor(service.color)}`}><service.icon size={24} /></div>
            {isSelected && <CheckCircle2 size={24} className={`text-${service.color}-600`}/>}
        </div>
        <div>
            {/* CÓDIGO CORREGIDO PARA LA ETIQUETA DE DESCUENTO EN SERVICECARD */}
            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                {service.title}
                {service.hasDiscount && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-extrabold animate-pulse">
                        {service.id.includes('afiliaciones') 
                            ? 'DESC. VOLUMEN' 
                            : service.id === 'seleccion' 
                                ? 'VALOR DINÁMICO' 
                                : 'ZAPHY FULL (15% OFF)'
                        }
                    </span>
                )}
            </h4>
            <p className="text-xs text-gray-500 mt-1 leading-snug">{service.desc}</p>
            
            <button 
                onClick={onToggleDetails}
                className="text-[10px] font-bold text-gray-400 hover:text-purple-600 mt-2 flex items-center gap-1 uppercase tracking-wide"
            >
                {isOpen ? 'Ocultar detalles' : 'Ver qué incluye'} {isOpen ? <ChevronUp size={10}/> : <ChevronDown size={10}/>}
            </button>
            
            {isOpen && service.details && (
                <ul className="mt-2 text-xs text-gray-600 space-y-1 bg-gray-50 p-2 rounded-lg list-disc ml-4 border border-gray-100 animate-[fadeIn_0.2s]">
                    {service.details.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                    ))}
                </ul>
            )}
        </div>
        
        {showQuantity && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg p-2" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-500 pl-2 uppercase">Cantidad:</span>
                    <div className="flex items-center gap-2">
                        <button onClick={onDecrease} className="p-1 hover:bg-gray-100 rounded text-gray-600"><Minus size={14}/></button>
                        <span className="font-bold text-sm w-4 text-center">{quantity}</span>
                        <button onClick={onIncrease} className="p-1 hover:bg-gray-100 rounded text-gray-600"><Plus size={14}/></button>
                    </div>
                </div>

                {/* BLOQUE DE SALARIOS PARA SELECCIÓN */}
                {service.id === 'seleccion' && isSelected && (
                    <div className="mt-3 space-y-3 border-t border-pink-100 pt-3 animate-[fadeIn_0.3s]">
                        {Array.from({ length: quantity }).map((_, i) => {
                            const currentSal = selectionSalaries[i + 1];
                            let individualPrice = 0;

                            if (currentSal > 0) {
                                if (currentSal > 10000000) {
                                    individualPrice = 4000000;
                                } else if (currentSal <= SMLV_2026) {
                                    individualPrice = 950000;
                                } else {
                                    const factor = (currentSal - SMLV_2026) / (10000000 - SMLV_2026);
                                    individualPrice = 950000 + (factor * (4000000 - 950000));
                                }

                                {currentSal > 10000000 && (
                                <a 
                                    href="https://wa.me/+573114044203" 
                                    target="_blank" 
                                    className="block text-center bg-green-500 text-white text-[10px] py-1 rounded mt-1 font-bold animate-bounce"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    HABLAR CON ASESOR EXPERTO
                                </a>
                            )}
                            }
                            return (
                                <div key={i} className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-pink-100 text-pink-700 text-[10px] font-bold px-2 py-1 rounded">#{i + 1}</div>
                                        <input 
                                            type="number"
                                            className="flex-1 text-xs p-2 border border-gray-200 rounded-lg outline-none bg-gray-50 focus:ring-2 focus:ring-pink-300"
                                            placeholder="Indique salario"
                                            value={currentSal === undefined ? '' : currentSal}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setSelectionSalaries(prev => ({ 
                                                    ...prev, 
                                                    [i + 1]: val === '' ? undefined : parseFloat(val) 
                                                }));
                                            }}
                                        />
                                    </div>
                                    <p className="text-[10px] text-right text-pink-500 font-bold">
                                        Costo: {formatCurrency(individualPrice)}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
            <div className="flex flex-col">
                <span className="text-xs font-semibold bg-gray-100 px-2 py-1 rounded text-gray-600 w-max">{service.unit}</span>
                {service.note && (
                    <span className="text-[10px] text-gray-400 mt-1 italic">
                        {service.note}
                    </span>
                )}
            </div>
            
            <div className="text-right">
                {service.hasDiscount && (
                    <span className="block text-xs text-gray-400 line-through decoration-red-500 decoration-2">
                        {formatCurrency(service.originalPrice)}
                    </span>
                )}
                <span className={`text-sm font-bold ${service.hasDiscount ? 'text-green-600 text-lg' : 'text-gray-800'}`}>
                    {formatCurrency(service.finalPrice)}
                </span>
            </div>
        </div>
    </div>
);

export default Cotizador;
